import React from 'react';
import {
  calcTotalMediaBudget,
  formatPercentage,
  getAdjustedEffectiveUnitPrice,
  getAdjustedUnitPrice,
  getUnitName
} from 'shared/src/domain/line-item/line-item-utils';
import { formatMoney } from 'shared/src/utils/money/money-utils';
import {
  DateItem,
  RightRailItem,
  RightRailSection,
  RightRailSubheading,
  ValueEmphasis,
  ValueWithSubvalue,
  ViewPacingSchedule
} from '@/components/display/right-rail-components';
import {
  formatCurrency,
  formatCurrencyNoDecimal,
  formatPacingType
} from '@/components/tables/table-utils';
import { diffInDays } from '@/metrics/calculate-metrics';
import {
  filterAdOps,
  filterAnalysts,
  filterClientSuccessDirectors,
  filterSeniorMediaTraders
} from 'shared/src/utils/user/user-utils';
import { ISODateString } from 'shared/src/date-string-utils/types';
import { toISODateString } from 'shared/src/date-string-utils/date-utils';
import { UTCDate } from '@date-fns/utc';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleInfo,
  faCommentDollar,
  faArrowUpRightFromSquare
} from '@fortawesome/pro-light-svg-icons';
import { Note } from '@/icons/note';
import { CampaignV2 } from 'shared/src/api/campaigns-v2';
import { DBStrategy } from 'shared/src/types';
import { useCampaignContext } from '@/features/campaigns/campaign-context';
import {
  isAgencyMarkupStrategy,
  isAgencyMarkupZohoCampaign,
  isReferralCommissionStrategy,
  isReferralCommissionZohoCampaign
} from '@/features/strategy/strategy-utils';
import { ExecutionLineItemTrpcResponse } from 'shared/src/trpc-types/line-item-trpc-types';
import { useChannelRatesContext } from '@/features/strategy/channel-rates-context';
import { formatLineItemType } from 'shared/src/domain/line-item/line-item-types';
import { StrategyTrpcResponse } from 'shared/src/trpc-types/strategy-trpc-types';
import { Channel } from 'shared/src/domain/line-item/line-item-channels';
import { BaseLineItem } from 'shared/src/api/line-item-types';
import { tsOmit } from 'shared/src/utils/object/object-utils';

type Props = { isStrategyDefined: boolean; strategy: DBStrategy };

export function CampaignDetailsRail({ isStrategyDefined, strategy }: Props) {
  const { campaign } = useCampaignContext();
  const {
    campaign_number,
    budget,
    proposed_budget,
    flight_date,
    end_date,
    goals_kpis,
    account_name,
    account_industry,
    agency_name,
    agency_gross_markup,
    referral_commission,
    referral_partner_name,
    referral_partner_comm
  } = campaign;
  const lengthInDays = getLengthInDays(flight_date, end_date);

  const isAgencyZohoCampaign = isAgencyMarkupZohoCampaign(campaign);
  const isReferralZohoCampaign = isReferralCommissionZohoCampaign(campaign);
  const zohoUrl = `https://crm.zoho.com/crm/org643932192/tab/Potentials/${campaign.campaign_id}`;

  return (
    <div className="flex flex-col gap-1">
      <a
        href={zohoUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-blue-700">
        <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="h-3 w-3" />
        View in Zoho
      </a>
      <div className="flex flex-col gap-2 rounded-md bg-gray-100 p-1">
        <RightRailSubheading>Campaign Details</RightRailSubheading>
        <RightRailSection>
          <div className="flex grow flex-col gap-0.5">
            <RightRailItem label="CN Number | Zoho" value={campaign_number} />
            <RightRailItem
              label="Proposed Budget | Zoho"
              value={formatCurrency(proposed_budget || 0)}
            />
            <RightRailItem
              label="Revenue | Zoho"
              value={budget ? formatCurrency(budget) : '$— —'}
            />
          </div>
        </RightRailSection>
        <RightRailSection>
          <div className="flex grow flex-col gap-0.5">
            <DateItem
              label="Start Date | Zoho"
              date={flight_date ? toISODateString(flight_date) : null}
            />
          </div>
          <div className="flex grow flex-col gap-0.5">
            <DateItem label="End Date | Zoho" date={end_date ? toISODateString(end_date) : null} />
          </div>
          <div className="grow-2 flex flex-col gap-0.5">
            <RightRailItem
              label="Duration | Zoho"
              value={`${lengthInDays.toLocaleString()} days`}
            />
          </div>
        </RightRailSection>
        <RightRailSection>
          <div className="flex grow flex-col gap-0.5">
            <RightRailItem label="Client goal | Zoho" value={goals_kpis} />
            <>
              {!isStrategyDefined && isAgencyZohoCampaign && <AgencyMarkupPanel />}
              <RightRailItem
                label="Agency Gross Markup % | Zoho"
                value={(agency_gross_markup || 0) + '%'}
              />
              {isStrategyDefined && !agencyMarkupValuesMatch(strategy, campaign) && (
                <AgencyMarkupDiscrepancyPanel proposal={strategy} campaign={campaign} />
              )}
            </>
          </div>
        </RightRailSection>
        {/* Account Details Section */}
        <RightRailSubheading>Account Details</RightRailSubheading>
        <RightRailSection>
          <div className="flex grow flex-col gap-0.5">
            <RightRailItem label="Account | Zoho" value={account_name} />
            <RightRailItem label="Industry | Zoho" value={account_industry || '-'} />
            <RightRailItem label="Agency | Zoho" value={agency_name || '-'} />
          </div>
        </RightRailSection>
        <>
          {!isStrategyDefined && isReferralZohoCampaign && <ReferralRatePanel />}
          <RightRailSection>
            <div className="flex grow flex-col gap-0.5">
              <RightRailItem
                label="Referral Commission | Zoho"
                value={referral_commission ? `Yes` : 'No'}
              />
              <RightRailItem label="Referral Partner | Zoho" value={referral_partner_name || '-'} />
              <RightRailItem
                label="Referral Partner Comm % | Zoho"
                value={(referral_partner_comm ?? 0) + '%'}
              />
              {isStrategyDefined && !referralCampaignValuesMatch(strategy, campaign) && (
                <ReferralRateDiscrepancyPanel proposal={strategy} campaign={campaign} />
              )}
            </div>
          </RightRailSection>
        </>
      </div>
    </div>
  );
}

function ReferralRatePanel() {
  return (
    <div className="3 flex rounded-md bg-blue-100">
      <div className="mr-3 flex shrink-0 items-center justify-center rounded-l-md bg-blue-200 px-1 py-3 text-center">
        <FontAwesomeIcon icon={faCommentDollar} className="h-5 w-5 text-blue-600" />
      </div>
      <div className="flex-1 pr-2">
        <p className="py-2 text-sm text-blue-900">
          Referral detected for this campaign. Confirm the rate in Zoho. Bravo will configure the
          fields and calculations for a Referral Campaign.
        </p>
      </div>
    </div>
  );
}

type DiscrepancyProps = {
  proposal: DBStrategy;
  campaign: CampaignV2;
};

function ReferralRateDiscrepancyPanel({ proposal, campaign }: DiscrepancyProps) {
  return (
    <div className="3 flex rounded-md bg-yellow-100">
      <div className="mr-3 flex shrink-0 items-center justify-center rounded-l-md bg-yellow-200 px-1 py-3 text-center">
        <FontAwesomeIcon icon={faCircleInfo} className="h-5 w-5 text-gray-800" />
      </div>
      <div className="flex-1 py-1 pr-2">
        <p className="font-semibold">Campaign Referral Rate Discrepancy</p>
        <p className="py-1 text-sm text-gray-800">
          {formatPercentage(proposal.referral_commission_rate ?? 0, 0)} Referral Rate set for this
          campaign in Bravo doesn't match the{' '}
          {formatPercentage(campaign.referral_partner_comm ?? 0, 0)} Commission | Zoho
        </p>
      </div>
    </div>
  );
}

function AgencyMarkupPanel() {
  return (
    <div className="3 flex rounded-md bg-blue-100">
      <div className="mr-3 flex shrink-0 items-center justify-center rounded-l-md bg-blue-200 px-1 py-3 text-center">
        <FontAwesomeIcon icon={faCommentDollar} className="h-5 w-5 text-blue-600" />
      </div>
      <div className="flex-1 pr-2">
        <p className="py-2 text-sm text-blue-900">
          Agency markup detected for this campaign. Confirm the rate in Zoho. Bravo will configure
          the fields and calculations for an Agency Markup Campaign.
        </p>
      </div>
    </div>
  );
}

function AgencyMarkupDiscrepancyPanel({ proposal, campaign }: DiscrepancyProps) {
  return (
    <div className="3 flex rounded-md bg-yellow-100">
      <div className="mr-3 flex shrink-0 items-center justify-center rounded-l-md bg-yellow-200 px-1 py-3 text-center">
        <FontAwesomeIcon icon={faCircleInfo} className="h-5 w-5 text-gray-800" />
      </div>
      <div className="flex-1 py-1 pr-2">
        <p className="font-semibold">Campaign Agency Markup Rate Discrepancy</p>
        <p className="py-1 text-sm text-gray-800">
          {formatPercentage(proposal.agency_markup_rate ?? 0, 0)} Agency Markup Rate set for this
          campaign in Bravo doesn't match the {(campaign.agency_gross_markup ?? 0) + '%'} Agency
          Gross Markup | Zoho
        </p>
      </div>
    </div>
  );
}

export function CampaignRoleAssignmentsRail() {
  const { campaign } = useCampaignContext();
  const seniorMediaTraders = filterSeniorMediaTraders(campaign.team);
  const csds = filterClientSuccessDirectors(campaign.team);
  const analysts = filterAnalysts(campaign.team);

  return (
    <div className="flex flex-col gap-1 rounded-md bg-gray-100 p-1">
      <RightRailSection>
        <RightRailItem
          label="Customer Success Director | Zoho"
          value={csds.length > 0 ? csds[0].name : null}
        />
      </RightRailSection>
      <RightRailSection>
        <RightRailItem label="Lead Account Manager | Zoho" value={'NA'} />
      </RightRailSection>
      <RightRailSection>
        <RightRailItem
          label="Senior Media Trader | Zoho"
          value={seniorMediaTraders.length > 0 ? seniorMediaTraders[0].name : null}
        />
      </RightRailSection>
      <RightRailSection>
        <RightRailItem
          label="Ad Ops | Zoho"
          value={filterAdOps(campaign.team)
            .map(user => user.name)
            .join(', ')}
        />
      </RightRailSection>
      <RightRailSection>
        <RightRailItem label="Analytics" value={analysts.length > 0 ? analysts[0].name : null} />
      </RightRailSection>
    </div>
  );
}

type MediaPlanDetailsRailProps = {
  strategy: StrategyTrpcResponse;
  lineItem: ExecutionLineItemTrpcResponse;
  setExpanded: (expanded: string) => void;
};

export function DetailsRail({ strategy, lineItem, setExpanded }: MediaPlanDetailsRailProps) {
  const { channels } = useChannelRatesContext();
  const { campaign } = useCampaignContext();

  const { channel, tactic, unit_price_type } = getChannelTacticType(channels, lineItem);
  const { dates, line_item_type } = lineItem;

  const mediaBudget = lineItem.pacing_details
    ? calcTotalMediaBudget(
        lineItem.pacing_details,
        line_item_type,
        lineItem.planned_margin,
        lineItem.referral_commission_rate,
        strategy.referral_commission_rate
      )
    : lineItem.media_budget || 0;
  const unitCost = lineItem.planned_unit_cost;
  const totalUnits = unitCost && unitCost > 0 ? Math.round(mediaBudget / unitCost) : 0;
  const unitLabel = getUnitName(lineItem.unit_price_type_name || 'CPM').full;
  const lengthInDays = getLengthInDays(dates.from, dates.to);
  const isMgmtFee = line_item_type === 'management_fee';
  const isZeroDollar = line_item_type === 'zero_dollar';
  const isReferralStrategy = isReferralCommissionStrategy(strategy);
  const isAgencyStrategy = isAgencyMarkupStrategy(strategy);

  return (
    <div className="flex flex-col gap-2 rounded-md bg-gray-100 p-1">
      <RightRailSection>
        <RightRailItem
          label="Assigned Media Trader"
          value={lineItem.media_traders?.map(mt => mt.name).join(', ')}
        />
      </RightRailSection>
      <RightRailSubheading>Media Execution Details</RightRailSubheading>
      <RightRailSection>
        <div className="flex flex-1 flex-col gap-0.5">
          <div className="flex gap-0.5">
            <div className="grow-2 flex flex-col">
              <DateItem label="Start Date" date={dates.from} />
            </div>
            <div className="grow-2 flex flex-col">
              <DateItem label="End Date" date={dates.to} />
            </div>
            <div className="flex grow flex-col">
              <RightRailItem label="Duration" value={`${lengthInDays.toLocaleString()} days`} />
            </div>
          </div>
          <div className="flex gap-0.5">
            <RightRailItem
              label="Pacing Type"
              value={formatPacingType(lineItem.pacing_type)}
              cta={
                lineItem.pacing_type === 'custom' ? (
                  <ViewPacingSchedule openPacing={() => setExpanded('pacing')} />
                ) : null
              }
            />
          </div>
          <div className="flex gap-0.5">
            <RightRailItem
              label="Monthly Rollover"
              value={lineItem.monthly_rollover ? 'Yes' : 'No'}
            />
          </div>
        </div>
      </RightRailSection>
      <RightRailSection>
        <div className="flex flex-1 flex-col gap-0.5">
          <RightRailItem label="Channel : Tactic" value={`${channel.name} : ${tactic.name}`} />
          <RightRailItem
            label="Platforms"
            value={
              lineItem.media_platforms?.length > 0 ? (
                lineItem.media_platforms?.map(platform => platform.name).join(', ')
              ) : (
                <span className="text-red-600">None assigned</span>
              )
            }
          />
          <RightRailItem label="Unit Price Type" value={unit_price_type?.name} />
          <div className="flex gap-0.5">
            <RightRailItem label="Media Budget" value={formatMoney(mediaBudget)} />
            <RightRailItem label={`Total ${unitLabel}`} value={totalUnits.toLocaleString()} />
          </div>
        </div>
      </RightRailSection>
      {isMgmtFee && <ValueEmphasis>Managed Account</ValueEmphasis>}
      <RightRailSection>
        <div className="flex flex-1 flex-col gap-0.5">
          <div className="flex gap-0.5">
            <RightRailItem
              label="Planned Margin"
              value={
                lineItem.planned_margin != null
                  ? `${parseFloat((lineItem.planned_margin * 100).toFixed(2)).toString()}%`
                  : 'N/A'
              }
            />
            <RightRailItem
              label="Planned Unit Cost"
              value={
                <span>
                  {formatCurrency(
                    unit_price_type?.name === 'CPM'
                      ? (lineItem.planned_unit_cost || 0) * 1000
                      : lineItem.planned_unit_cost || 0
                  )}
                  {unit_price_type && (
                    <span className="text-xs font-normal"> {unit_price_type.name}</span>
                  )}
                </span>
              }
            />
          </div>
        </div>
      </RightRailSection>
      <RightRailSection>
        <div className="flex flex-1 flex-col gap-0.5">
          <RightRailItem label="Geo" value={lineItem.geo} />
          <RightRailItem label="Targeting" value={lineItem.targeting} />
          <RightRailItem label="Audience" value={lineItem.audience} />
        </div>
      </RightRailSection>
      {lineItem.notes && (
        <RightRailSection>
          <div className="flex flex-1 flex-col justify-between gap-2 rounded-sm bg-white px-2 py-2">
            <div className="flex items-center gap-3 text-sm font-semibold">
              <Note />
              Team Notes
            </div>
            {lineItem.notes}
          </div>
        </RightRailSection>
      )}
      <RightRailSubheading>Campaign & Pricing Details</RightRailSubheading>
      <RightRailSection>
        <div className="flex flex-1 gap-0.5">
          <div className="grow-2 flex">
            <RightRailItem
              label="Line Item Type"
              value={
                <ValueEmphasis className="mb-0.5 block max-w-fit">
                  {formatLineItemType(line_item_type)}
                </ValueEmphasis>
              }
            />
          </div>
          <div className="grow-2 flex">
            <RightRailItem
              label="Campaign Type"
              value={
                <ValueEmphasis className="mb-0.5 block max-w-fit">
                  {getStrategyType(isReferralStrategy, isAgencyStrategy)}
                </ValueEmphasis>
              }
            />
          </div>
        </div>
      </RightRailSection>
      <RightRailSection>
        <div className="flex flex-1 flex-col gap-0.5">
          <div className="flex flex-1 gap-0.5">
            <RightRailItem
              label="Price"
              value={
                <ValueWithSubvalue
                  value={formatCurrencyNoDecimal(lineItem.price)}
                  showSubvalue={isMgmtFee || isZeroDollar}
                  subvalue={isMgmtFee ? 'Fee' : 'Zero Dollar'}
                />
              }
            />
            <RightRailItem
              label="Unit Price"
              value={
                <span className="flex items-baseline gap-1">
                  {isMgmtFee ? (
                    'N/A'
                  ) : (
                    <>
                      {formatCurrency(getAdjustedUnitPrice({ ...lineItem, unit_price_type }) || 0)}
                      {isZeroDollar ? (
                        <span className="border-l border-gray-500 pl-1 font-normal text-gray-500">
                          Zero Dollar
                        </span>
                      ) : (
                        <span className="text-xs font-normal">{unit_price_type?.name}</span>
                      )}
                    </>
                  )}
                </span>
              }
            />
          </div>
          <div className="flex flex-1 gap-0.5">
            <RightRailItem
              label="Agency Markup %"
              value={formatPercentage(strategy.agency_markup_rate, 2)}
            />
          </div>
          {isAgencyStrategy && (
            <div className="flex flex-1 gap-0.5">
              <RightRailItem
                label="Gross Price"
                value={
                  <ValueWithSubvalue
                    value={formatCurrencyNoDecimal(lineItem.gross_price)}
                    showSubvalue={isMgmtFee}
                    subvalue="Fee"
                  />
                }
              />
              <RightRailItem
                label="Gross Unit Price"
                value={isMgmtFee ? 'N/A' : formatCurrencyNoDecimal(lineItem.gross_unit_price || 0)}
              />
            </div>
          )}
          <div className="flex flex-1 gap-0.5">
            <RightRailItem
              label="Referral Fee"
              value={formatPercentage(lineItem.referral_commission_rate, 2)}
            />
          </div>
          {isReferralStrategy && (
            <div className="flex flex-1 gap-0.5">
              <RightRailItem
                label="Effective Price"
                value={
                  <ValueWithSubvalue
                    value={formatCurrencyNoDecimal(lineItem.effective_price)}
                    showSubvalue={isMgmtFee}
                    subvalue="Fee"
                  />
                }
              />
              <RightRailItem
                label="Effective Unit Price"
                value={
                  isMgmtFee
                    ? 'N/A'
                    : formatCurrency(
                        getAdjustedEffectiveUnitPrice({ ...lineItem, unit_price_type }) || 0
                      )
                }
              />
            </div>
          )}
        </div>
      </RightRailSection>
      <RightRailSection>
        <div className="flex flex-1 flex-col gap-0.5">
          <RightRailItem label="Client goal" value={campaign.goals_kpis} />
          <RightRailItem label="Account" value={campaign.account_name} />
          <RightRailItem label="Agency" value={campaign.agency_name} />
        </div>
      </RightRailSection>
    </div>
  );
}

function getLengthInDays(
  startDate: ISODateString | UTCDate | null,
  endDate: ISODateString | UTCDate | undefined | null
) {
  if (!startDate || !endDate) return 0;
  const start = typeof startDate === 'string' ? new UTCDate(startDate) : startDate;
  const end = typeof endDate === 'string' ? new UTCDate(endDate) : endDate;
  return diffInDays(start, end);
}

function referralCampaignValuesMatch(proposal: DBStrategy, campaign: CampaignV2): boolean {
  return (campaign.referral_partner_comm || 0) === proposal.referral_commission_rate * 100;
}

function agencyMarkupValuesMatch(proposal: DBStrategy, campaign: CampaignV2): boolean {
  return (campaign.agency_gross_markup || 0) === proposal.agency_markup_rate * 100;
}

function getStrategyType(isReferral: boolean, isAgencyMarkup: boolean) {
  if (isReferral && isAgencyMarkup) return 'Referral+Agency Markup';
  if (isReferral) return 'Referral';
  if (isAgencyMarkup) return 'Agency Markup';
  return 'Standard';
}

function getChannelTacticType(channels: Channel[], lineItem: BaseLineItem) {
  const channel = channels.find(c => c.id === lineItem.channel_id);
  if (!channel) throw new Error('Channel not found');
  const tactic = channel.tactics.find(t => t.id === lineItem.tactic_id);
  if (!tactic) throw new Error('Tactic not found');
  const unit_price_type = tactic.rates.find(
    r => r.unitPriceType.id === lineItem.unit_price_type_id
  )?.unitPriceType;

  return {
    channel: tsOmit(channel, ['tactics']),
    tactic: tsOmit(tactic, ['platforms', 'rates']),
    unit_price_type
  };
}
